import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ProjectRouteStation } from '../../shared/interfaces/project-route-station.interface';
import { ProjectGroup } from '../../shared/interfaces/project-group.interface';
import { ProjectGroupMetaInformation } from '../../shared/interfaces/project-group-meta-information';
import { ProjectTimeSwitchBody } from '../../project-time-switch/shared/interfaces/project-time-switch-body';
import { TimerSwitchService } from '../../shared/services/timer-switch.service';
import { AssignmentConflictDialogComponent, AssignmentConflictRow } from '../conflict-dialog/assignment-conflict-dialog.component';
import { timerTypeBlocks } from '../shared/time-switch-component-types';
import { StationAssignDialogComponent, StationAssignDialogResult } from '../station-assign-dialog/station-assign-dialog.component';

/**
 * Zeigt die einer Zeitschaltuhr zugewiesenen Stationen (nach Gruppe gruppiert, mit Typ-Blöcken
 * und Metainformation) und öffnet den Zuweisungsdialog (pro Station pro Typ).
 */
@Component({
  selector: 'avs-time-switch-assignment-panel',
  templateUrl: './time-switch-assignment-panel.component.html',
  styleUrls: ['./time-switch-assignment-panel.component.css']
})
export class TimeSwitchAssignmentPanelComponent implements OnChanges {
  @Input() timer?: ProjectTimeSwitchBody;
  @Input() projectStations: ProjectRouteStation[] = [];
  @Input() projectGroups: ProjectGroup[] = [];
  @Input() metaByGroup: Map<number, ProjectGroupMetaInformation[]> = new Map();

  public assignedGuids = new Set<string>();
  public assignedTypes = new Map<string, string[]>();

  constructor(
    private readonly timerService: TimerSwitchService,
    private readonly dialog: MatDialog
  ) { }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['timer']) { this.loadAssignedStations(); }
  }

  /** Aktuell zugewiesene Stationen, nach Name sortiert. */
  public get assignedStations(): ProjectRouteStation[] {
    return this.projectStations
      .filter(station => this.assignedGuids.has(station.id))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  /** Zugewiesene Stationen nach Gruppe gruppiert (Reihenfolge der Projektgruppen). */
  public get assignedGroups(): { name: string, stations: ProjectRouteStation[] }[] {
    const byGroup = new Map<number, ProjectRouteStation[]>();
    for (const station of this.assignedStations) {
      if (!byGroup.has(station.groupId)) { byGroup.set(station.groupId, []); }
      byGroup.get(station.groupId)!.push(station);
    }
    const groups: { name: string, stations: ProjectRouteStation[] }[] = [];
    for (const group of this.projectGroups) {
      const stations = byGroup.get(group.id);
      if (stations && stations.length > 0) {
        groups.push({ name: group.name || group.routeName || ('Gruppe ' + group.id), stations });
        byGroup.delete(group.id);
      }
    }
    for (const [groupId, stations] of byGroup) {
      groups.push({ name: 'Gruppe ' + groupId, stations });
    }
    return groups;
  }

  public metaForStation(station: ProjectRouteStation): ProjectGroupMetaInformation[] | undefined {
    return this.metaByGroup.get(station.groupId);
  }

  public assignedTypeBlocks(station: ProjectRouteStation): { label: string, color: string, text: string }[] {
    return timerTypeBlocks(this.assignedTypes.get(station.id) ?? []);
  }

  private loadAssignedStations(): void {
    const id = this.timer?.id;
    if (!id) { this.assignedGuids = new Set<string>(); this.assignedTypes = new Map(); return; }
    this.timerService.getAssignedStations(id).subscribe(entries => {
      this.assignedGuids = new Set<string>((entries ?? []).map(e => e.routeStationId));
      this.assignedTypes = new Map((entries ?? []).map(e => [e.routeStationId, e.types ?? []]));
    });
  }

  public removeAssignment(station: ProjectRouteStation): void {
    const id = this.timer?.id;
    if (!id) { return; }
    this.timerService.unassignFromGuids(id, [station.id]).subscribe(() => this.loadAssignedStations());
  }

  public openAssignDialog(): void {
    const id = this.timer?.id;
    if (!id) { return; }
    const stations = [...this.projectStations].sort((a, b) => a.name.localeCompare(b.name));
    const guids = stations.map(s => s.id);

    this.timerService.getStationCapabilities(id, guids).subscribe(caps => {
      const capabilities = new Map<string, string[]>((caps ?? []).map(c => [c.routeStationId, c.types ?? []]));
      const dialogRef = this.dialog.open(StationAssignDialogComponent, {
        width: '860px',
        data: {
          stations,
          projectGroups: this.projectGroups,
          metaByGroup: this.metaByGroup,
          capabilities,
          current: this.assignedTypes
        }
      });
      dialogRef.afterClosed().subscribe((result: StationAssignDialogResult | undefined) => {
        if (!result) { return; }
        this.timerService.setAssignments(id, result.assignments).subscribe(res => {
          this.loadAssignedStations();
          this.reportConflicts(res?.conflicts);
        });
      });
    });
  }

  private reportConflicts(conflicts?: { routeStationId: string, types: string[] }[]): void {
    if (!conflicts || conflicts.length === 0) { return; }
    const rows: AssignmentConflictRow[] = conflicts.map(c => {
      const station = this.projectStations.find(s => s.id === c.routeStationId);
      return {
        name: station?.name ?? c.routeStationId,
        types: c.types ?? [],
        station,
        metaInformation: station ? this.metaForStation(station) : undefined
      };
    });
    this.dialog.open(AssignmentConflictDialogComponent, { width: '520px', data: { rows } });
  }
}
